/**
 * Lightweight Markdown → TipTap JSON importer. Covers what Notion / Obsidian /
 * Apple-Notes-exported-as-Markdown / generic `.md` files produce: headings,
 * bullet + ordered + task lists, blockquotes, fenced code, dividers, and the
 * common inline marks (bold / italic / inline code / links). No dependency.
 */
import type { JSONContent } from "@tiptap/core";

const INLINE = /(\*\*[^*]+\*\*|__[^_]+__|\*[^*]+\*|_[^_]+_|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;

/** Parse a single line's inline marks into TipTap text nodes. */
function inline(text: string): JSONContent[] {
  if (!text) return [];
  const nodes: JSONContent[] = [];
  let last = 0;
  for (const m of text.matchAll(INLINE)) {
    const idx = m.index ?? 0;
    if (idx > last) nodes.push({ type: "text", text: text.slice(last, idx) });
    const tok = m[0];
    if (tok.startsWith("**") || tok.startsWith("__")) {
      nodes.push({ type: "text", text: tok.slice(2, -2), marks: [{ type: "bold" }] });
    } else if (tok.startsWith("`")) {
      nodes.push({ type: "text", text: tok.slice(1, -1), marks: [{ type: "code" }] });
    } else if (tok.startsWith("[")) {
      const label = tok.slice(1, tok.indexOf("]"));
      const href = tok.slice(tok.indexOf("(") + 1, -1);
      nodes.push({ type: "text", text: label, marks: [{ type: "link", attrs: { href } }] });
    } else {
      nodes.push({ type: "text", text: tok.slice(1, -1), marks: [{ type: "italic" }] });
    }
    last = idx + tok.length;
  }
  if (last < text.length) nodes.push({ type: "text", text: text.slice(last) });
  return nodes;
}

const para = (text: string): JSONContent =>
  text.trim() ? { type: "paragraph", content: inline(text) } : { type: "paragraph" };

export function markdownToContent(md: string): JSONContent {
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const content: JSONContent[] = [];
  let i = 0;

  const flushList = (
    ordered: boolean,
    task: boolean
  ): JSONContent => {
    const items: JSONContent[] = [];
    const bulletRe = /^\s*[-*+]\s+(.*)$/;
    const taskRe = /^\s*[-*+]\s+\[( |x|X)\]\s+(.*)$/;
    const orderedRe = /^\s*\d+\.\s+(.*)$/;
    while (i < lines.length) {
      const line = lines[i];
      if (task) {
        const m = taskRe.exec(line);
        if (!m) break;
        items.push({
          type: "taskItem",
          attrs: { checked: m[1].toLowerCase() === "x" },
          content: [{ type: "paragraph", content: inline(m[2]) }],
        });
      } else if (ordered) {
        const m = orderedRe.exec(line);
        if (!m) break;
        items.push({ type: "listItem", content: [{ type: "paragraph", content: inline(m[1]) }] });
      } else {
        if (taskRe.test(line)) break; // a task list starts — let the caller handle it
        const m = bulletRe.exec(line);
        if (!m) break;
        items.push({ type: "listItem", content: [{ type: "paragraph", content: inline(m[1]) }] });
      }
      i++;
    }
    return task
      ? { type: "taskList", content: items }
      : { type: ordered ? "orderedList" : "bulletList", content: items };
  };

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    if (/^```/.test(line)) {
      const lang = line.replace(/^```/, "").trim();
      const buf: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) buf.push(lines[i++]);
      i++; // closing fence
      content.push({
        type: "codeBlock",
        attrs: lang ? { language: lang } : {},
        content: buf.length ? [{ type: "text", text: buf.join("\n") }] : [],
      });
      continue;
    }
    // Horizontal rule
    if (/^\s*([-*_])\1{2,}\s*$/.test(line)) {
      content.push({ type: "horizontalRule" });
      i++;
      continue;
    }
    // Heading
    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    if (h) {
      content.push({ type: "heading", attrs: { level: h[1].length }, content: inline(h[2]) });
      i++;
      continue;
    }
    // Blockquote
    const q = /^>\s?(.*)$/.exec(line);
    if (q) {
      content.push({ type: "blockquote", content: [para(q[1])] });
      i++;
      continue;
    }
    // Task list
    if (/^\s*[-*+]\s+\[( |x|X)\]\s+/.test(line)) {
      content.push(flushList(false, true));
      continue;
    }
    // Ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      content.push(flushList(true, false));
      continue;
    }
    // Bullet list
    if (/^\s*[-*+]\s+/.test(line)) {
      content.push(flushList(false, false));
      continue;
    }
    // Blank line
    if (!line.trim()) {
      i++;
      continue;
    }
    // Paragraph (merge consecutive non-blank, non-structural lines)
    const buf: string[] = [line];
    i++;
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^(#{1,3}\s|>|```|\s*[-*+]\s|\s*\d+\.\s|\s*([-*_])\2{2,}\s*$)/.test(lines[i])
    ) {
      buf.push(lines[i++]);
    }
    content.push(para(buf.join(" ")));
  }

  return { type: "doc", content: content.length ? content : [{ type: "paragraph" }] };
}
